/*
 * Product variant recovery script.
 *
 * This theme export referenced this asset from layout/theme.liquid, but the
 * file was empty. The product templates depend on the custom <variant-selects>
 * element to keep the selected Shopify variant, price, media, and product form
 * in sync.
 */
(() => {
  'use strict';

  const formatMoney = (amount) => {
    const currency = window.Shopify?.currency?.active || 'CLP';
    const locale = document.documentElement.lang || 'es-CL';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount / 100);
  };

  class VariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const variantJson = this.querySelector('script[type="application/json"]');
      if (!variantJson) return;

      try {
        this.variantData = JSON.parse(variantJson.textContent);
      } catch (error) {
        console.error('Unable to read product variant data.', error);
        return;
      }

      this.addEventListener('change', this.onChange.bind(this));
      this.syncSelectedVariant();
    }

    onChange() {
      this.syncSelectedVariant();
    }

    getSelectedOptions() {
      return Array.from(this.querySelectorAll('select, input[type="radio"]:checked')).map((input) => input.value);
    }

    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();

      return this.variantData.find((variant) =>
        variant.options.length === selectedOptions.length &&
        variant.options.every((option, index) => option === selectedOptions[index])
      );
    }

    syncSelectedVariant() {
      const variant = this.getSelectedVariant();
      if (!variant) return;

      const sectionId = this.dataset.section;
      const productForm = document.getElementById(`product-form-${sectionId}`);

      if (productForm) {
        productForm.querySelectorAll('input[name="id"]').forEach((input) => {
          input.value = variant.id;
          input.disabled = false;
        });

        const addButton = productForm.querySelector('button[name="add"]');
        if (addButton) addButton.disabled = !variant.available;
      }

      document
        .querySelectorAll(`#product-form-installment-${sectionId} input[name="id"]`)
        .forEach((input) => { input.value = variant.id; });

      this.updatePrice(sectionId, variant);
      this.updateMedia(sectionId, variant);
      this.updateUrl(variant);

      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant },
      }));
    }

    updatePrice(sectionId, variant) {
      const priceRoot = document.getElementById(`price-${sectionId}`);
      if (!priceRoot) return;

      const formattedPrice = formatMoney(variant.price);
      priceRoot
        .querySelectorAll('.price__regular .price-item, .price__sale .price-item--sale')
        .forEach((element) => { element.textContent = formattedPrice; });

      const addToCartPrice = document.getElementById(`main-atc-price-${sectionId}`);
      // The theme leaves this element empty when “Display price in button” is
      // disabled. Respect that setting instead of forcing a price into the
      // Add to Cart label.
      if (addToCartPrice && addToCartPrice.textContent.trim()) {
        addToCartPrice.textContent = `${formattedPrice} •`;
      }
    }

    updateMedia(sectionId, variant) {
      if (!variant.featured_media?.id) return;

      const mediaId = `${sectionId}-${variant.featured_media.id}`;
      document.querySelectorAll(`media-gallery[data-section="${sectionId}"]`).forEach((gallery) => {
        if (typeof gallery.setActiveMedia !== 'function') return;
        if (!gallery.querySelector(`[data-media-id="${mediaId}"]`)) return;
        gallery.setActiveMedia(mediaId, false);
      });
    }

    updateUrl(variant) {
      if (this.dataset.updateUrl === 'false' || !this.dataset.url) return;

      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }

  if (!customElements.get('variant-selects')) {
    customElements.define('variant-selects', VariantSelects);
  }
})();